import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import promotionService, {
  PromotionRule,
  PromotionDiscountType,
  PromotionScope,
  ALL_DAYS_MASK,
  DAY_BITS,
} from '../../services/promotionService';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import { getServerErrorMessage } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './PromotionListPage.css';
import { CirclePlus, RefreshCw, SquarePen, Trash2, BadgePercent, CircleCheck, CircleSlash } from 'lucide-react';

/** Day keys in the backend's bit order — Mon(bit 0) .. Sun(bit 6). */
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const PromotionListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();

  const [promotions, setPromotions] = useState<PromotionRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? null
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const { items } = await promotionService.getAll();
      setPromotions(items);
    } catch (err) {
      setError(getServerErrorMessage(err) || t('promotion.failedToLoad'));
    }
  }, [t]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  // Clear the one-shot banner from navigation state so a browser refresh does not replay it.
  useEffect(() => {
    if ((location.state as { message?: string } | null)?.message) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const handleDelete = async (promotion: PromotionRule) => {
    if (!window.confirm(t('promotion.confirmDelete', { name: promotion.name }))) return;
    try {
      await promotionService.delete(promotion.id);
      setSuccessMessage(t('promotion.deleteSuccess'));
      await load();
    } catch (err) {
      setError(getServerErrorMessage(err) || t('promotion.failedToDelete'));
    }
  };

  const formatDiscount = (promotion: PromotionRule) =>
    promotion.discountType === PromotionDiscountType.Percentage
      ? `${promotion.discountValue}%`
      : new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2 }).format(promotion.discountValue);

  /** 0 and a full mask both mean "runs every day" — show the shorter label for both. */
  const formatDays = (activeDays: number) => {
    if (!activeDays || activeDays === ALL_DAYS_MASK) return t('promotion.everyDay');
    return DAY_KEYS.filter((_, index) => (activeDays & DAY_BITS[index]) !== 0)
      .map((key) => t(`promotion.dayShort.${key}`))
      .join(', ');
  };

  /** Times arrive as "HH:mm:ss"; the seconds carry no meaning for a promotion window. */
  const formatWindow = (promotion: PromotionRule) => {
    if (!promotion.timeFrom || !promotion.timeTo) return t('promotion.allDay');
    return `${promotion.timeFrom.slice(0, 5)} – ${promotion.timeTo.slice(0, 5)}`;
  };

  const scopeLabel = (scope: PromotionScope) => {
    if (scope === PromotionScope.Categories) return t('promotion.scopeCategories');
    if (scope === PromotionScope.Products) return t('promotion.scopeProducts');
    return t('promotion.scopeAllProducts');
  };

  const columns: GridColumn[] = [
    {
      key: 'name',
      label: t('promotion.name'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'discountValue',
      label: t('promotion.discount'),
      sortable: true,
      width: '120px',
      render: (_value, row) => formatDiscount(row as PromotionRule),
    },
    {
      key: 'scope',
      label: t('promotion.scope'),
      sortable: true,
      width: '150px',
      render: (_value, row) => {
        const promotion = row as PromotionRule;
        const count =
          promotion.scope === PromotionScope.Categories
            ? promotion.categoryIds.length
            : promotion.scope === PromotionScope.Products
              ? promotion.productIds.length
              : 0;
        return (
          <span className="description-text">
            {scopeLabel(promotion.scope)}
            {count > 0 ? ` (${count})` : ''}
          </span>
        );
      },
    },
    {
      key: 'activeDays',
      label: t('promotion.schedule'),
      width: '220px',
      render: (_value, row) => {
        const promotion = row as PromotionRule;
        return (
          <span className="description-text">
            {formatDays(promotion.activeDays)} · {formatWindow(promotion)}
          </span>
        );
      },
    },
    {
      key: 'priority',
      label: t('promotion.priority'),
      sortable: true,
      width: '100px',
    },
    {
      key: 'isActive',
      label: t('common.status'),
      width: '120px',
      render: (value) =>
        value ? (
          <span className="ims-badge ims-badge-approved">{t('common.active')}</span>
        ) : (
          <span className="ims-badge ims-badge-closed">{t('common.inactive')}</span>
        ),
    },
  ];

  const actions: GridAction[] = [
    ...(hasPermission(Permissions.Promotions.Edit) ? [{
      icon: <SquarePen size={16} strokeWidth={1.75} />,
      onClick: (item: any) => navigate(`/sales/promotions/${item.id}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    }] : []),
    ...(hasPermission(Permissions.Promotions.Delete) ? [{
      icon: <Trash2 size={16} strokeWidth={1.75} />,
      onClick: (item: any) => handleDelete(item as PromotionRule),
      className: 'grid-action-btn-delete',
      title: t('common.delete'),
    }] : []),
  ];

  const activeCount = promotions.filter((p) => p.isActive).length;

  return (
    <Layout>
      <div className="container promotion-page">
        <div className="header">
          <h1 className="title">{t('promotion.title')}</h1>
          <div className="header-actions">
            {hasPermission(Permissions.Promotions.Create) && (
              <button
                className="btn-new"
                onClick={() => navigate('/sales/promotions/create')}
                title={t('promotion.newPromotion')}
              >
                <CirclePlus />
                {t('promotion.newPromotion')}
              </button>
            )}
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon promotion-icon-total"><BadgePercent size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{promotions.length}</span>
              <span className="ims-stat-label">{t('promotion.totalPromotions')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon promotion-icon-active"><CircleCheck size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{activeCount}</span>
              <span className="ims-stat-label">{t('common.active')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon promotion-icon-inactive"><CircleSlash size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{promotions.length - activeCount}</span>
              <span className="ims-stat-label">{t('common.inactive')}</span>
            </div>
          </div>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show">
            {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={promotions}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('promotion.noPromotionsFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default PromotionListPage;
